import { z } from 'zod';
import type { CanalEnvio, ProvedorEnvio, SituacaoEnvio, TipoDestinatario, TipoEventoEnvio, TipoFalhaEnvio } from '../enums/dominios';

// Painel de envios com falha (RQ-MSG-009 a 011): e-mails e mensagens de chat que não foram enviados, com resumo por
// tipo de falha, filtros, detalhe e reenvio (mensagem nova ligada à original, até 3 por original).

/** Reenvios de uma mesma mensagem original (decisão do responsável, 25/10/2026). */
export const MAXIMO_REENVIOS = 3;

const lista = (v: unknown) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v);
const booleano = (v: unknown) => (v === 'true' || v === 'false' ? v === 'true' : v);
const dia = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Situação do reenvio na lista: originais ainda sem reenvio, originais já reenviadas, ou só os reenvios. */
export type FiltroReenvio = 'nao-reenviadas' | 'reenviadas' | 'reenvios';

/** Filtros do painel (na URL, como texto). Empresas e "sem empresa" só valem para o superadministrador. */
export const esquemaFiltroEnvios = z.object({
  de: dia.optional(),
  ate: dia.optional(),
  canal: z.coerce.number().int().min(1).max(2).optional(),
  provedor: z.coerce.number().int().min(1).max(5).optional(),
  tipoFalha: z.coerce.number().int().min(1).max(99).optional(),
  reenviavel: z.preprocess(booleano, z.boolean().optional()),
  reenvio: z.enum(['nao-reenviadas', 'reenviadas', 'reenvios']).optional(),
  destinatario: z.string().trim().max(320).optional(),
  empresas: z.preprocess(lista, z.array(z.string().uuid()).max(200).optional()),
  semEmpresa: z.preprocess(booleano, z.boolean().optional()),
  pagina: z.coerce.number().int().min(1).max(10_000).optional(),
});
export type FiltroEnvios = z.infer<typeof esquemaFiltroEnvios>;

/** Reenviar as escolhidas (uuids) ou todas as que podem ser reenviadas dentro de um filtro (ex.: um tipo de falha). */
export const esquemaReenvio = z
  .object({ uuids: z.array(z.string().uuid()).min(1).max(500).optional(), filtro: esquemaFiltroEnvios.optional() })
  .refine((r) => Boolean(r.uuids) !== Boolean(r.filtro), 'Informe as mensagens ou um filtro.');
export type ReenvioEntrada = z.infer<typeof esquemaReenvio>;

export type EmpresaDoEnvioDto = { uuid: string; nome: string };

/** Uma linha do resumo do alto do painel. */
export type ResumoFalhaDto = { tipoFalha: TipoFalhaEnvio; total: number; podemReenviar: number; ultimaEm: string };

export type EnvioComFalhaDto = {
  uuid: string;
  canal: CanalEnvio;
  empresa: EmpresaDoEnvioDto | null;
  categoria: string | null;
  /** Endereços "Para" do e-mail ou o nome de quem recebe a mensagem de chat. */
  destinatarios: string[];
  /** Assunto do e-mail ou o começo do texto do chat. */
  assunto: string;
  provedor: ProvedorEnvio | null;
  situacao: SituacaoEnvio;
  tipoFalha: TipoFalhaEnvio | null;
  motivo: string | null;
  reenviavel: boolean | null;
  tentativas: number;
  maxTentativas: number;
  criadoEm: string;
  falhouEm: string | null;
  /** 0 na original; 1 a 3 nos reenvios. */
  numeroReenvio: number;
  /** Reenvios já feitos a partir da original (contador da original). */
  reenvios: number;
  /** Uuid da original, nos reenvios. */
  originalUuid: string | null;
  /** O reenvio mais recente da cadeia, com a situação dele (na original). */
  ultimoReenvio: { uuid: string; situacao: SituacaoEnvio } | null;
  /** O botão "Reenviar" desta linha (age na original). */
  podeReenviar: boolean;
};

export type EnviosComFalhaDto = {
  resumo: ResumoFalhaDto[];
  envios: EnvioComFalhaDto[];
  total: number;
  pagina: number;
  porPagina: number;
  /** Só para o superadministrador: as empresas que ele pode escolher no filtro. */
  empresas: EmpresaDoEnvioDto[] | null;
};

export type DetalheEnvioDto = EnvioComFalhaDto & {
  remetente: string | null;
  responderPara: string | null;
  criadoPor: string | null;
  /** Corpo oculto nos convites (levam um link de acesso). */
  corpoOculto: boolean;
  corpoHtml: string | null;
  corpoTexto: string | null;
  anexos: { nome: string; tamanhoBytes: number }[];
  enderecos: { endereco: string; tipo: TipoDestinatario; situacao: SituacaoEnvio }[];
  historico: { numero: number; provedor: ProvedorEnvio | null; sucesso: boolean; tipoFalha: TipoFalhaEnvio | null; erro: string | null; ocorridaEm: string }[];
  eventos: { tipo: TipoEventoEnvio; destinatario: string | null; motivo: string | null; ocorridoEm: string }[];
  /** A original e cada reenvio, em ordem. */
  cadeia: { uuid: string; numeroReenvio: number; situacao: SituacaoEnvio; criadoEm: string; criadoPor: string | null; motivo: string | null }[];
};

export type ResultadoReenvioDto = { reenviadas: number; ignoradas: { uuid: string; motivo: string }[] };
